import React, { useEffect, useState } from 'react'
import {
	Box,
	Title,
	Text,
	Card,
	Group,
	Badge,
	Button,
	Switch,
	SimpleGrid,
	Center,
	Loader,
} from '@mantine/core'
import { useNavigate, useParams } from 'react-router-dom'
import {
	getMessageSetups,
	updateMessageSetup,
} from '../services/actions/experienceActions'
import {
	STOCK_TEMPLATES,
	findSetupForTemplate,
	isTemplateConfigured,
} from '../constants/stockTemplates'

const getSetupData = (setup) => {
	if (!setup?.data || typeof setup.data !== 'object' || Array.isArray(setup.data)) {
		return {}
	}
	return setup.data
}

const EditExperienceMessageSetupsPage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const navigate = useNavigate()

	const [messageSetups, setMessageSetups] = useState([])
	const [loading, setLoading] = useState(true)
	const [switchLoadingByTemplate, setSwitchLoadingByTemplate] = useState({})
	const [error, setError] = useState('')

	useEffect(() => {
		const fetchMessageSetups = async () => {
			try {
				setLoading(true)
				setError('')
				const setups = await getMessageSetups(experienceSlug, orgSlug)
				setMessageSetups(setups)
			} catch (err) {
				console.error('Error loading message setups', err)
				setError(err.message || 'Failed to load message templates')
			} finally {
				setLoading(false)
			}
		}

		fetchMessageSetups()
	}, [experienceSlug, orgSlug])

	const goToTemplateEditor = (templateKey) => {
		navigate(
			`/${orgSlug}/experiences/${experienceSlug}/manage/messagesetups/${templateKey}`,
		)
	}

	const getTemplateActiveValue = (existingSetup, configured) => {
		if (!configured) return false
		return getSetupData(existingSetup).isActive !== false
	}

	const handleTemplateActiveToggle = async ({
		template,
		existingSetup,
		configured,
		nextValue,
	}) => {
		if (nextValue && !configured) {
			alert('Configure this template before setting it active.')
			return
		}

		if (!existingSetup?.id) {
			alert('Save this template before changing active status.')
			return
		}

		const previousValue = getTemplateActiveValue(existingSetup, configured)
		const previousData = getSetupData(existingSetup)
		const nextData = { ...previousData, isActive: nextValue }

		setSwitchLoadingByTemplate((prev) => ({ ...prev, [template.key]: true }))
		setMessageSetups((prev) =>
			prev.map((setup) =>
				setup.id === existingSetup.id
					? { ...setup, data: nextData }
					: setup,
			),
		)

		try {
			const updatedSetup = await updateMessageSetup(
				orgSlug,
				experienceSlug,
				existingSetup.id,
				{ data: nextData },
			)
			setMessageSetups((prev) =>
				prev.map((setup) =>
					setup.id === updatedSetup.id ? updatedSetup : setup,
				),
			)
		} catch (err) {
			setMessageSetups((prev) =>
				prev.map((setup) =>
					setup.id === existingSetup.id
						? {
								...setup,
								data: { ...previousData, isActive: previousValue },
						  }
						: setup,
				),
			)
			alert(err.message || 'Failed to update template status')
		} finally {
			setSwitchLoadingByTemplate((prev) => ({ ...prev, [template.key]: false }))
		}
	}

	return (
		<Box px='md' py='md'>
			<Title order={2}>Edit Experience Message Templates</Title>
			<Text c='dimmed' mb='md'>
				Configure stock templates used across the experience lifecycle.
			</Text>

			{loading ? (
				<Center py='xl'>
					<Loader />
				</Center>
			) : error ? (
				<Text c='red'>{error}</Text>
			) : (
				<SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
					{STOCK_TEMPLATES.map((template) => {
						const existingSetup = findSetupForTemplate(messageSetups, template)
						const configured = isTemplateConfigured(
							template,
							existingSetup?.data,
							existingSetup,
						)
						const isActive = getTemplateActiveValue(existingSetup, configured)

						return (
							<Card
								key={template.key}
								withBorder
								shadow='sm'
								radius='md'
								p='lg'
							>
								<Group position='apart' mb='sm'>
									<Badge
										color={template.type === 'on_demand' ? 'blue' : 'orange'}
										variant='light'
									>
										{template.type}
									</Badge>
									<Badge color={configured ? 'green' : 'gray'} variant='dot'>
										{configured ? 'Configured' : 'Not configured'}
									</Badge>
								</Group>

								<Title order={4} mb={6}>
									{template.name}
								</Title>
								<Text size='sm' c='dimmed' mb='md'>
									{template.description}
								</Text>
								<Group position='apart' align='center'>
									<Button
										variant='light'
										onClick={() => goToTemplateEditor(template.key)}
									>
										Edit
									</Button>
									{template.type === 'event_driven' && (
										<Switch
											checked={isActive}
											disabled={
												switchLoadingByTemplate[template.key] ||
												(!configured && !isActive)
											}
											label={isActive ? 'Active' : 'Inactive'}
											onChange={(event) =>
												handleTemplateActiveToggle({
													template,
													existingSetup,
													configured,
													nextValue: event.currentTarget.checked,
												})
											}
										/>
									)}
								</Group>
							</Card>
						)
					})}
				</SimpleGrid>
			)}
		</Box>
	)
}

export default EditExperienceMessageSetupsPage
